// ============================================================================
// HELP CONTENT - Edit this file to update help text for each page
// Location: frontend/src/data/helpContent.js
// Last Updated: February 9, 2026 - Synced with Implementation Guide v27
//   Added: Pairings/Balanced Flights, Private tee times, Bulk creation,
//   Shotgun mode, Referral/Share, Subscription info, PWA, SMS 320-char,
//   Forgot Password, GHIN auto-sync, linked orgs
// ============================================================================
// 
// HOW TO EDIT:
// 1. Find the page you want to update (e.g., "teeTimes", "players", etc.)
// 2. Edit the "content" field with your help text
// 3. You can use simple line breaks with \n\n for paragraphs
// 4. Save the file - changes appear immediately (may need page refresh)
//
// ============================================================================

export const helpContent = {
  // -------------------------------------------------------------------------
  // MAIN TABS
  // -------------------------------------------------------------------------
  
  teeTimes: {
    title: "📊 Tee Times",
    content: `This is your main dashboard for managing golf outings. Use this as a general reference and also as your GETTING STARTED guide. Each page has help specific to that page so be sure to click and read.

GETTING STARTED WITH TEE TIME ORGANIZER (Recommended Path)
1. Start with COURSES - Enter the courses that you typically play.
2. Add upcoming TEE TIMES - Quick and easy to do as just four data points are required.
3. Add PLAYERS - You can load the basic information like name and email address for each regular player in the group and they can complete their profile later.
4. PUBLISH individual tee times to the group.
5. Send a WELCOME EMAIL - Admin → Messages → Welcome Email. Players receive a link to their personal portal.
6. RELAX! Players RSVP from their portal. Automatic reminders go out 24 hours before and same-day.

HERE ARE SOME OF THE FEATURES HIGHLIGHTED (Detailed help available on the specific page)

CREATING TEE TIMES
Click "+ Add Tee Time" to create a new outing. You have three options:
• Simple - Create a single tee time (most common)
• Recurring - Create multiple tee times at once (e.g., every Tuesday for 12 weeks)
• Bulk - Enter start and end times to create multiple tee times on the same date (great for golf outings where players choose their start time)

TEE TIME STATUS
• Draft - Only visible to organizers. New tee times start as Draft by default.
• Published - Visible to all active players in their portal and available for RSVP.
• Private - Not visible to players except those the organizer manually adds via Manage Players. Useful for invitation-only rounds.

MANAGING PLAYERS
Click "Manage Players" on any tee time to add or remove players. You can add players individually or select an entire player group. When a tee time is full, additional players are automatically added to the waitlist.

PUBLISHING & NOTIFICATIONS
Draft tee times are only visible to you. Click "Publish" to make a tee time visible in the Player Portal and optionally send email/SMS notifications to your players.

PAIRINGS & TEE SHEETS
Navigate to the Pairings tab to set your player groups and generate professional tee sheets. Options include Randomize, Sort by HCP, and Balanced Flights (A/B/C/D tier distribution for fair foursomes). You can also use Shotgun mode for simultaneous starts.

FILTERS & VIEWS
Use the filter buttons to show Upcoming, Today, Past, or All tee times. Switch between Card and List view using the toggle. Use the search box to find tee times by course name or player name.

INVITE LINKS
Click the link icon on any tee time to generate a shareable invite link. Anyone with the link can RSVP directly to that specific tee time and will be added as a guest.`
  },

  admin: {
    title: "⚙️ Admin Panel",
    content: `The Admin Panel is where you navigate the app and manage your organization's data and settings.

PLAYERS TAB
Add, edit, and manage your player database. Each player gets a unique portal link for self-service RSVPs. Players have three roles: Organizer, Player, and Guest.

COURSES TAB
Set up the golf courses your group plays. Course information appears on tee times and in player notifications. You can also configure course tees (rating, slope, par) for handicap calculations.

GROUPS TAB
Create player groups like "Tuesday Regulars" or "Weekend Warriors" for quick bulk assignments to tee times and targeted notifications.

MESSAGE TAB
Customize email and SMS templates for notifications, reminders, and confirmations. Use variables like {player_name}, {date}, {tee_sheet}, and more for personalization. Send custom messages to all players, specific groups, or individuals.

REPORTS TAB
View statistics about tee times, player participation, and course usage. Export data to CSV for your records.`
  },

  settings: {
    title: "🏢 Organization Settings",
    content: `Configure your organization's profile and preferences.

ORGANIZATION PROFILE
Update your organization name and details. This information appears in email notifications sent to your players.

EMAIL CONFIGURATION
Configure your email sending settings. You can customize the "from" name and reply-to email that appears on outgoing emails. Each organization can have its own reply-to address so player replies go to the right person.

ORGANIZATION ID
Your unique Organization ID can be shared with players who belong to multiple golf groups, allowing them to link accounts and see tee times from all their organizations in one portal.

TEAM MEMBERS (CO-ORGANIZERS)
Invite other players to help manage your organization as co-organizers.

Adding a Co-Organizer:
1. First, ensure the person exists in your Players database (Admin → Players)
2. Enter their email address in the Team Members section
3. Click "Send Invite" to send them an invitation email
4. They click the link in the email to set up their password
5. Once accepted, they can log in and access the organizer dashboard

IMPORTANT: The invitee must already be a player in your database. This prevents typos and unauthorized access. If you get an error that the email wasn't found, add the person as a player first.

Managing Team Members:
• View all current team members and their roles
• See pending invitations that haven't been accepted yet
• Resend invitation emails if needed
• Remove team members (except the organization owner)

Co-organizers have full access to create and manage tee times, players, courses, and settings for your organization.

PREFERENCES
Set default values and preferences that apply to your entire organization:

• TIME ZONE - Important for sending reminders at the correct local time.
• EMAIL REMINDERS - Enable automatic reminders and choose distribution times.
• WEATHER - Include weather forecasts with wind speed and direction in reminders.
• TEE SHEET OPTIONS - Configure player order display: RSVP order, randomized, or manually sorted (see page-specific help for details).

SUBSCRIPTION & BILLING
View your current plan, billing cycle, and player count vs. plan limit. Upgrade, downgrade, or cancel from this section. Access the Stripe billing portal to update payment methods and view invoices.

• Plans are based on active player count (Starter, Basic, Professional, Enterprise)
• 30-day free trial for new organizations
• Annual plans save up to 20% vs. monthly

REFERRAL PROGRAM
Earn free months by referring other golf clubs to Tee Time Organizer.

Your Referral Code:
Each organization has a unique referral code displayed in this section. Click "Copy" to copy it to your clipboard for easy sharing.

How It Works:
1. Share your referral code with other golf clubs
2. When they sign up and subscribe for 6+ months, you BOTH earn 1 free month
3. There's no limit to how many referrals you can make

Sharing Your Referral:
• Use the Share button in Settings → Referral Program to send a pre-written invitation email directly from TTO
• Players can also share from the Share button in their Player Portal header
• Copy the link and share via text, social media, or your own email

Referral Credits:
Your earned referral credits are displayed in this section. These free months are automatically applied to your subscription.`
  },

  // -------------------------------------------------------------------------
  // ADMIN SUB-TABS
  // -------------------------------------------------------------------------

  players: {
    title: "👥 Players",
    content: `Manage your organization's player database.

ADDING PLAYERS
Click "+ Add Player" to create a new player. Required fields are name and email. Phone, GHIN number, and handicap are optional but helpful. Players can complete their own profile later via the Player Portal.

PLAYER PORTAL LINKS
Each player automatically gets a unique magic link for accessing their Player Portal. Click the link icon to copy a player's portal URL, or use "Copy Link" or "Copy Short URL" to copy the link for that player's app access. The SHORT URL is recommended as it automatically redirects to the longer, more secure URL.

IMPORTANT: Players access their portal via their magic link with no password required. These links should never be shared with anyone other than the player, as the recipient could add, edit, or delete tee time reservations.

EDITING PLAYERS
Click any player row to view their full profile. Click "Edit" to update their information. Players can also update their own profile through the Player Portal.

PLAYER STATUS
Mark players as inactive if they're no longer participating. Inactive players won't appear in player selection lists but their history is preserved. Use deactivate rather than delete to preserve historical data.

PLAYER ROLES
There are three player roles:
• Organizer - Gets access to the organizer dashboard. Typically one or two per group.
• Player - Regular members who receive new tee time notifications and can RSVP via their portal.
• Guest - Invited players who play occasionally. Guests do not receive new tee time notifications and cannot see unpublished tee times in their portal. Guests are hidden by default in the player list (use the "Show Guests" checkbox to display them).

PLAYER PREFERENCES
Players can set several preferences in their portal profile:
• PREFERRED TEE - For course handicap calculations on tee sheets
• EMAIL NOTIFICATIONS - Opt in/out of email communications
• SMS NOTIFICATIONS - Opt in to receive text message reminders (requires valid phone number and explicit consent)
• GHIN REMINDER - Opt in/out of post-round reminders to post scores
• CANCELLATION NOTIFICATIONS - Opt in to receive notifications when tee times are cancelled

ADDING TO HOME SCREEN
Encourage players to add TTO to their phone's home screen for app-like quick access:
• iPhone: Open portal link in Safari → Share button (⬆️) → Add to Home Screen
• Android: Open in Chrome → Menu (⋮) → Add to Home Screen

BULK ACTIONS
Use "Send Bulk Invites" to email portal links to multiple players at once. Export your player list to CSV for backup or external use.`
  },

  courses: {
    title: "⛳ Courses",
    content: `Set up the golf courses your group plays.

ADDING COURSES
Click "+ Add Course" and search first: type at least 3 letters of the course name to search more than 25,000 courses. Picking one brings in its tees, ratings, slopes and hole data. If your course isn't there, enter the course name, city, state and zip code. The location is used for weather forecasts on tee times.

COURSE DETAILS
Include accurate location information so weather forecasts display correctly, formatted as "City, State" (full state name). The phone number is helpful should you need to call the pro shop. The email address is used when sending tee sheets to the course.

COURSE TEES (FOR HANDICAPS)
Open a course with Edit to set up its tees for course handicaps and scoring.

From a scorecard photo (quickest):
1. Under Course Tees, click "📷 Import from Scorecard"
2. Take or choose a photo of the scorecard: flat, well lit, the whole card in the frame
3. In 10 to 15 seconds TTO reads every tee: name, course rating, slope, par, and each hole's par, yardage and handicap
4. If the card doesn't print a rating or slope, those tees wait in the Scorecard import panel. Type in each tee's course rating and slope (pro shop, or the course lookup on ghin.com) and save. A tee without both numbers isn't saved.
5. Compare each tee's hole data with the card

If the import asks for a JPG or PNG, take a screenshot of the photo and import that. For a two-sided card, import each side.

Adding Tees by hand:
1. Click "+ Add Tee" in the Course Tees section
2. Enter the tee name (e.g., "Gold", "Blue", "White")
3. Select a color for visual display on tee sheets
4. Enter the Course Rating (e.g., 72.4)
5. Enter the Slope Rating (e.g., 131)
6. Enter the Par (typically 72)
7. Fill in its Hole Data: par, yardage and hole handicap for every hole (scoring and games need them)

Where to Find Tee Data:
• Course scorecard
• Course website
• Ask the pro shop
• GHIN/USGA course database

Why Set Up Tees?
When tees are configured, TTO can display accurate course handicaps on tee sheets using the World Handicap System (WHS) formula:
Course HCP = (Handicap Index × Slope ÷ 113) + (Course Rating - Par)

Handicaps are used by the Balanced Flights pairing method to create fair foursomes with A/B/C/D tier distribution. Players without a handicap are assigned to the D tier by default.

EDITING COURSES
Click any course to edit its details. Changes apply to future tee times using that course.

DELETING COURSES
Courses can only be deleted if no tee times are associated with them. If you need to remove a course that has tee times, first reassign or delete those tee times.`
  },

  groups: {
    title: "👨‍👩‍👧‍👦 Player Groups",
    content: `Create groups of players for quick assignments and communications.

WHAT ARE GROUPS?
Groups let you organize players who frequently play together. Examples: "Tuesday Morning Regulars", "Weekend Warriors", "Beginners Clinic". When publishing tee times, you may elect to notify only specific groups.

CREATING GROUPS
Click "+ Add Group" to create a new group. Give it a descriptive name and add members from your player database.

USING GROUPS
When adding players to a tee time, you can select an entire group with one click. All group members will be added (if slots are available). Groups are also available when publishing tee times to target notifications.

MANAGING MEMBERSHIP
Players can belong to multiple groups. Add or remove members anytime by editing the group. Removing a player from a group doesn't affect their tee time registrations.

GROUP COMMUNICATIONS
When publishing a tee time, you can choose to notify specific groups rather than all players. Groups are also available in the Player Message tool for sending custom messages to subsets of your players.`
  },

  message: {
    title: "✉️ Message Templates",
    content: `Customize the emails and SMS messages sent to your players.

TEMPLATE TYPES
• New Tee Time Published - Sent when you publish a tee time
• 24-Hour Reminder - Automatic reminder the day before
• Same-Day Reminder - Morning-of reminder
• Waitlist Opening - Sent when a spot opens up
• Cancellation Notice - Sent if a tee time is cancelled
• Welcome Email - Sent to new players
• GHIN Posting Reminder - Sent after rounds to remind players to post scores

USING VARIABLES
Insert dynamic content using variables:
• {player_name} - Player's full name
• {player_first_name} - Player's first name
• {date} - Tee time date
• {time} - Tee time start time
• {course_name} - Course name
• {weather} - Weather forecast (if available)
• {player_portal_url} - Link to player's portal
• {tee_sheet} - Formatted tee sheet with player pairings
• {tee_sheet_with_hcp} - Tee sheet including course handicaps
• {handicap_guide_link} - Link to a lighthearted handicap education page

EDITING TEMPLATES
Click any template to edit its subject line and body. Keep subject lines under 50 characters for best display in email clients.

PREVIEW
Use the preview feature to see how your email will look with sample data before saving.

GHIN POSTING REMINDER
Configure when the GHIN reminder is sent by setting the delay hours (e.g., 5 hours after tee time). The system will check again in 24 hours and send a second reminder if the score hasn't been posted. Players can opt out of this reminder in their profile settings.

SMS TEXT MESSAGES
For templates that support SMS, you can also configure a short text message version (320 characters max, 2 SMS segments). Players who have opted in to SMS will receive both email and text reminders based on their preferences. SMS requires explicit player consent — the opt-in is unchecked by default.

PLAYER MESSAGE
Player Message is an effective way to communicate with all, some, or a group of players. Perfect for announcements, weather cancellations, frost delays, or group communications. Messages are logged in the Message Log for reference.`
  },

  reports: {
    title: "📈 Reports",
    content: `View statistics and analytics for your golf group.

TEE TIME STATS
See how many tee times you've created, published, and completed. Track fill rates and waitlist usage over time.

PLAYER PARTICIPATION
View which players are most active, who RSVPs quickly, and participation trends. Identify players who haven't played recently.

COURSE USAGE
See which courses are most popular with your group. Track how often each course is used.

DATE RANGE FILTERS
Filter reports by date range to analyze specific time periods. Compare month-over-month or year-over-year activity.

EXPORT DATA
Export report data to CSV for further analysis in spreadsheet software or for your records.

MESSAGE LOG
View all sent communications in Tools → Message Log:
• See every email and SMS sent from your organization
• Filter by date, recipient, or message type
• Verify delivery status
• Track both email and SMS channels`
  },

  pairings: {
    title: "⛳ Pairings & Tee Sheet",
    content: `Create fair foursomes and generate professional tee sheets for your rounds.

PAIRING METHODS
The tee sheet offers several ways to arrange players into foursomes:
• RSVP ORDER (default) - Players appear in the order they signed up
• RANDOMIZED - Shuffles all players randomly into foursomes. Great for mixing things up. Click randomize multiple times to get different arrangements.
• SORT BY HCP - Orders all players from lowest to highest course handicap. Useful for seeing the field at a glance.
• BALANCED FLIGHTS - The most popular option for competitive fairness. TTO assigns each player a tier (A, B, C, or D) based on handicap, then distributes one player from each tier into every foursome.

HOW BALANCED FLIGHTS WORKS
1. Players are sorted by their course handicap (calculated from handicap index and course tee)
2. Players are divided into four tiers: A (lowest HCP), B, C, and D (highest HCP)
3. Each foursome receives one player from each tier using an A/B/C/D draft
4. Tier badges (A, B, C, D) display next to each player's name
5. Supports both foursomes (4 per group) and twosomes (2 per team, 4 per hole)
6. If the number of players isn't evenly divisible by 4, remaining players form groups of 3 or 5

Players without a handicap are assigned to the D tier by default. For best results, encourage all players to keep their handicap and preferred tee current.

SHOTGUN MODE
Toggle shotgun mode for simultaneous starts. After pairing, groups are automatically assigned starting holes (Hole 1, Hole 2, etc.). Starting holes can be manually adjusted.

MANUAL REORDERING
Use the ▲/▼ arrows next to any player to manually override the auto-sort order.

HANDICAPS & COURSE TEES
• Display player handicaps with colored tee badges
• Change a player's tee using the dropdown next to their name
• Course handicaps are calculated using the WHS formula
• Requires course tees to be configured (Admin → Courses)

EMAILING TEE SHEETS
Click "Email Tee Sheet" to share with:
• Proshop - Send directly to the course (requires proshop email in course settings)
• Players on Tee Sheet - Email all players currently listed
• Select Players - Choose specific players from your database

You can add a custom message that will appear below the player lineup. You can also include the tee sheet in reminder templates using the {tee_sheet} or {tee_sheet_with_hcp} variables.`
  }
};

// ============================================================================
// HELPER FUNCTION - Gets content based on active tab
// ============================================================================
export const getHelpContent = (mainTab, subTab = null) => {
  // If we're on the admin tab with a sub-tab, show sub-tab content
  if (mainTab === 'admin' && subTab) {
    return helpContent[subTab] || helpContent.admin;
  }
  
  // Otherwise show main tab content
  return helpContent[mainTab] || { 
    title: "Help", 
    content: "No help content available for this page." 
  };
};

export default helpContent;
